import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getProducts, getWarehouses, createMovement, getRecentMovements } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowDownRight, ArrowUpRight, Search, Plus, Minus, Trash2, Printer, Package, Camera, X, Layers, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';

export default function FastStock() {
    const { user, company } = useAuth();
    const searchInputRef = useRef(null);
    const scannerRef = useRef(null);

    const [mode, setMode] = useState('IN');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [products, setProducts] = useState([]);
    const [allVariants, setAllVariants] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState('');
    const [cart, setCart] = useState([]);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [scannerReady, setScannerReady] = useState(false);
    const [recentOps, setRecentOps] = useState([]);
    const [isProduction, setIsProduction] = useState(false);

    const [variantPicker, setVariantPicker] = useState(null);
    const [variantQtys, setVariantQtys] = useState({});

    useEffect(() => { loadData(); }, []);
    useEffect(() => { if (!loading && searchInputRef.current && !showScanner) searchInputRef.current.focus(); }, [loading, showScanner]);

    useEffect(() => {
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            const variantMatch = allVariants.find(v => v.barcode?.toLowerCase() === q);
            if (variantMatch) {
                addVariantToCart(variantMatch, 1);
                setSearchQuery(''); setSearchResults([]); return;
            }
            const exactMatch = products.find(p => p.barcode?.toLowerCase() === q);
            if (exactMatch) {
                handleProductSelect(exactMatch);
                setSearchQuery(''); setSearchResults([]); return;
            }
            // Reference exact match
            const refMatch = products.find(p => p.reference?.toLowerCase() === q);
            if (refMatch) {
                handleProductSelect(refMatch);
                setSearchQuery(''); setSearchResults([]); return;
            }
            setSearchResults(products.filter(p =>
                p.name.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q) || p.reference?.toLowerCase().includes(q)
            ).slice(0, 8));
        } else setSearchResults([]);
    }, [searchQuery, products, allVariants]);

    useEffect(() => {
        if (showScanner && !scannerRef.current) {
            const scanner = new Html5Qrcode("scanner-container");
            scannerRef.current = scanner;
            scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 150 } },
                (text) => handleBarcodeScanned(text), () => {}
            ).then(() => setScannerReady(true)).catch(() => { toast.error('Kamera acilamadi'); closeScanner(); });
        }
        return () => { if (scannerRef.current?.isScanning) scannerRef.current.stop().catch(() => {}); };
    }, [showScanner]);

    const handleBarcodeScanned = (barcode) => {
        const bc = barcode.toLowerCase();
        const variantMatch = allVariants.find(v => v.barcode?.toLowerCase() === bc);
        if (variantMatch) { addVariantToCart(variantMatch, 1); toast.success(`${variantMatch.name} eklendi`); closeScanner(); return; }
        const product = products.find(p => p.barcode?.toLowerCase() === bc);
        if (product) { handleProductSelect(product); toast.success(`${product.name}`); }
        else { setSearchQuery(barcode); toast.info(`Barkod: ${barcode}`); }
        closeScanner();
    };

    const closeScanner = () => {
        if (scannerRef.current?.isScanning) scannerRef.current.stop().then(() => { scannerRef.current = null; setScannerReady(false); setShowScanner(false); }).catch(() => { scannerRef.current = null; setScannerReady(false); setShowScanner(false); });
        else { scannerRef.current = null; setScannerReady(false); setShowScanner(false); }
    };

    const loadData = async () => {
        try {
            const [pRes, wRes] = await Promise.all([getProducts(), getWarehouses()]);
            setProducts(pRes.data);
            setWarehouses(wRes.data);
            const def = wRes.data.find(w => w.is_default) || wRes.data[0];
            if (def) setSelectedWarehouse(def.id);
            const variants = [];
            for (const p of pRes.data) {
                if (p.variants?.length) {
                    for (const v of p.variants) variants.push({ ...v, product_id: p.id, product_name: p.name });
                }
            }
            setAllVariants(variants);
            getRecentMovements().then(r => setRecentOps(r.data)).catch(() => {});
        } catch { toast.error('Veriler yuklenemedi'); }
        finally { setLoading(false); }
    };

    const handleProductSelect = (product) => {
        if (product.variants?.length === 1) {
            // Single variant: add directly without picker
            const v = product.variants[0];
            addVariantToCart({ ...v, product_id: product.id, product_name: product.name }, 1);
            setSearchQuery(''); setSearchResults([]);
            searchInputRef.current?.focus();
        } else if (product.variants?.length > 1) {
            const qtys = {};
            product.variants.forEach(v => { qtys[v.id] = 0; });
            setVariantQtys(qtys);
            setVariantPicker(product);
            setSearchQuery(''); setSearchResults([]);
        } else {
            addProductToCart(product);
        }
    };

    const addProductToCart = (product) => {
        const packInfo = product.is_pack_based && product.pack_content ? product.pack_content : null;
        const bpp = product.boxes_per_pallet || null;
        setCart(prev => {
            const key = `product-${product.id}`;
            const ex = prev.find(i => i.key === key);
            if (ex) return prev.map(i => i.key === key ? { ...i, quantity: i.quantity + 1 } : i);
            return [...prev, { key, product_id: product.id, variant_id: null, name: product.name, sku: product.reference, barcode: product.barcode, quantity: 1, current_stock: product.current_stock || 0, pack_content: packInfo, boxes_per_pallet: bpp, pallets: '' }];
        });
        setSearchQuery(''); setSearchResults([]);
        searchInputRef.current?.focus();
    };

    const addVariantToCart = (variant, qty) => {
        if (qty <= 0) return;
        // Find parent product for boxes_per_pallet
        const parentProduct = products.find(p => p.id === variant.product_id);
        const bpp = parentProduct?.boxes_per_pallet || null;
        setCart(prev => {
            const key = `variant-${variant.id}`;
            const ex = prev.find(i => i.key === key);
            if (ex) return prev.map(i => i.key === key ? { ...i, quantity: i.quantity + qty } : i);
            return [...prev, { key, product_id: variant.product_id, variant_id: variant.id, name: `${variant.product_name} - ${variant.name}`, sku: variant.sku, barcode: variant.barcode, quantity: qty, current_stock: variant.current_stock || 0, pack_content: null, boxes_per_pallet: bpp, pallets: '' }];
        });
    };

    const confirmVariantPicker = () => {
        if (!variantPicker) return;
        const toAdd = Object.entries(variantQtys).filter(([, qty]) => qty > 0);
        if (!toAdd.length) { toast.error('En az 1 varyanta miktar girin'); return; }
        toAdd.forEach(([vid, qty]) => {
            const v = variantPicker.variants.find(vv => vv.id === vid);
            if (v) addVariantToCart({ ...v, product_id: variantPicker.id, product_name: variantPicker.name }, qty);
        });
        setVariantPicker(null);
        setVariantQtys({});
        searchInputRef.current?.focus();
    };

    const updateQty = (key, d) => setCart(prev => prev.map(i => i.key === key ? { ...i, quantity: Math.max(1, i.quantity + d), pallets: '' } : i));
    const setQty = (key, v) => setCart(prev => prev.map(i => i.key === key ? { ...i, quantity: Math.max(0, parseInt(v) || 0), pallets: '' } : i));
    const setPallets = (key, palletCount) => {
        setCart(prev => prev.map(i => {
            if (i.key !== key) return i;
            const bpp = i.customBpp ?? i.boxes_per_pallet;
            if (!bpp) return i;
            const p = Math.max(0, parseInt(palletCount) || 0);
            return { ...i, pallets: palletCount, quantity: p * bpp };
        }));
    };
    const setCustomBpp = (key, bppVal) => {
        setCart(prev => prev.map(i => {
            if (i.key !== key) return i;
            const bpp = parseInt(bppVal) || 0;
            const p = parseInt(i.pallets) || 0;
            return { ...i, customBpp: bppVal === '' ? '' : bpp, quantity: (p > 0 && bpp > 0) ? p * bpp : i.quantity };
        }));
    };
    const remove = (key) => setCart(prev => prev.filter(i => i.key !== key));
    const total = () => cart.reduce((s, i) => s + i.quantity, 0);

    const handleSave = async () => {
        if (!cart.length) return toast.error('Sepet bos');
        if (!selectedWarehouse) return toast.error('Depo secin');
        setSaving(true);
        try {
            await createMovement({
                movement_type: mode, warehouse_id: selectedWarehouse,
                is_production: isProduction,
                lines: cart.map(i => ({ product_id: i.product_id, warehouse_id: selectedWarehouse, quantity: i.quantity, variant_id: i.variant_id })),
                notes: notes || `Fast Stock ${mode}${isProduction ? ' (Uretim)' : ''}`
            });
            toast.success(`${mode === 'IN' ? 'Giris' : 'Cikis'} kaydedildi!`);
            setCart([]); setNotes('');
            getRecentMovements().then(r => setRecentOps(r.data)).catch(() => {});
            searchInputRef.current?.focus();
        } catch { toast.error('Kaydetme hatasi'); }
        finally { setSaving(false); }
    };

    const handlePrint = () => {
        if (!cart.length) return;
        const wh = warehouses.find(w => w.id === selectedWarehouse);
        const now = new Date();
        const rows = cart.map(i => {
            let info = `${i.name}`;
            if (i.pack_content) info += ` (${i.quantity} pkt = ${i.quantity * i.pack_content} adet)`;
            return `<div class="r"><span>${info}</span><span>${i.quantity}</span></div>`;
        }).join('');
        const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>*{margin:0;padding:0}body{font-family:monospace;font-size:12px;width:80mm;padding:5mm}.t{text-align:center;font-weight:bold;font-size:14px}.d{border-top:1px dashed #000;margin:8px 0}.r{display:flex;justify-content:space-between;margin:4px 0}</style></head><body><div class="t">STOK ${mode === 'IN' ? 'GIRIS' : 'CIKIS'}</div><div style="text-align:center">${now.toLocaleDateString('tr-TR')} ${now.toLocaleTimeString('tr-TR',{hour:'2-digit',minute:'2-digit'})}</div><div class="d"></div><div class="r"><span>Firma:</span><span>${company?.name||'-'}</span></div><div class="r"><span>Depo:</span><span>${wh?.name||'-'}</span></div><div class="d"></div>${rows}<div class="d"></div><div class="r" style="font-weight:bold"><span>TOPLAM:</span><span>${total()}</span></div><div class="d"></div><div style="text-align:center;font-size:10px">${user?.full_name||user?.username||'-'}</div><script>onload=()=>print()</script></body></html>`;
        const w = window.open('', '_blank', 'width=400,height=600');
        w.document.write(html); w.document.close();
    };

    if (loading) return <DashboardLayout><div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" /></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-4" data-testid="fast-stock-page">
                <div className="flex items-center justify-between">
                    <div><h1 className="page-title">Fast Stock</h1><p className="page-subtitle">Hizli stok giris/cikis</p></div>
                    <div className="flex items-center gap-3">
                        {warehouses.length > 1 && (
                            <select value={selectedWarehouse} onChange={e => setSelectedWarehouse(e.target.value)} className="form-select w-auto text-sm" data-testid="warehouse-select">
                                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                            </select>
                        )}
                        <span className="text-sm text-gray-400">{user?.full_name || user?.username}</span>
                    </div>
                </div>

                <div className="flex gap-3 items-center">
                    <button onClick={() => setMode('IN')} data-testid="mode-in-btn" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'IN' ? 'bg-green-50 text-green-600 ring-1 ring-green-200' : 'bg-gray-50 text-gray-500'}`}>
                        <ArrowDownRight size={16} /> IN
                    </button>
                    <button onClick={() => setMode('OUT')} data-testid="mode-out-btn" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'OUT' ? 'bg-red-50 text-red-500 ring-1 ring-red-200' : 'bg-gray-50 text-gray-500'}`}>
                        <ArrowUpRight size={16} /> OUT
                    </button>
                    <label className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all ${isProduction ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' : 'bg-gray-50 text-gray-400'}`} data-testid="production-toggle">
                        <input type="checkbox" checked={isProduction} onChange={e => setIsProduction(e.target.checked)} className="rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
                        Uretim
                    </label>
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input ref={searchInputRef} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && searchResults.length > 0) {
                                    e.preventDefault();
                                    handleProductSelect(searchResults[0]);
                                }
                            }}
                            placeholder="Barkod, SKU veya urun ara..." className="form-input pl-10 pr-10 text-sm" data-testid="search-input" />
                        <button type="button" onClick={() => setShowScanner(true)} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center" data-testid="scanner-btn"><Camera size={16} className="text-gray-500" /></button>
                        {searchResults.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                                {searchResults.map(p => (
                                    <div key={p.id} onClick={() => handleProductSelect(p)} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 cursor-pointer text-sm" data-testid={`search-result-${p.id}`}>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900 truncate">{p.name}</span>
                                                <span className="text-gray-400 text-xs">{p.reference || p.barcode}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                {p.variants?.length > 0 && <span className="text-blue-500 text-xs">({p.variants.length} varyant)</span>}
                                                {p.is_pack_based && p.pack_content && <span className="text-amber-500 text-xs">(paket: {p.pack_content})</span>}
                                                {p.boxes_per_pallet && <span className="text-purple-500 text-xs">(palet: {p.boxes_per_pallet} koli)</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 ml-3">
                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.current_stock <= 0 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`} data-testid={`search-stock-${p.id}`}>
                                                Stok: {p.current_stock ?? 0}
                                            </span>
                                            {p.variants?.length > 0 ? <Layers size={14} className="text-blue-400" /> : <Plus size={14} className="text-green-500" />}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Cart */}
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <table className="w-full mobile-cards" data-testid="cart-table">
                        <thead>
                            <tr className="text-xs font-medium text-gray-400 uppercase bg-gray-50 border-b border-gray-100">
                                <th className="text-left px-4 py-3">Urun</th>
                                <th className="text-left px-4 py-3">SKU</th>
                                <th className="text-center px-4 py-3">Palet</th>
                                <th className="text-center px-4 py-3">Miktar</th>
                                <th className="text-center px-4 py-3">Stok</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {cart.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm"><Package size={24} className="mx-auto mb-2 opacity-40" />Barkod tara veya urun ara</td></tr>
                            ) : cart.map(item => (
                                <tr key={item.key} className="border-b border-gray-50" data-testid={`cart-item-${item.key}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {item.variant_id ? <Layers size={12} className="text-blue-400" /> : null}
                                            <div>
                                                <div className="font-medium text-gray-900 text-sm">{item.name}</div>
                                                {item.pack_content && <div className="text-xs text-amber-600">{item.quantity} pkt = {item.quantity * item.pack_content} adet</div>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{item.sku || '-'}</td>
                                    <td className="px-4 py-3 text-center">
                                        {item.boxes_per_pallet ? (
                                            <div className="flex flex-col items-center gap-0.5">
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={item.pallets}
                                                        onChange={e => setPallets(item.key, e.target.value)}
                                                        placeholder="0"
                                                        className="w-12 h-7 text-center text-sm font-medium border border-blue-300 rounded bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                        data-testid={`pallet-input-${item.key}`}
                                                    />
                                                    <span className="text-[10px] text-blue-400">x</span>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={item.customBpp ?? item.boxes_per_pallet}
                                                        onChange={e => setCustomBpp(item.key, e.target.value)}
                                                        className="w-12 h-7 text-center text-sm font-medium border border-blue-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                        data-testid={`bpp-input-${item.key}`}
                                                    />
                                                </div>
                                                <span className="text-[10px] text-blue-500">koli/palet</span>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-center gap-1">
                                            <button onClick={() => updateQty(item.key, -1)} className="w-6 h-6 rounded border border-gray-200 flex items-center justify-center hover:bg-gray-50" data-testid={`qty-minus-${item.key}`}><Minus size={12} /></button>
                                            <input type="number" value={item.quantity || ''} onChange={e => setQty(item.key, e.target.value)} className="w-12 h-6 text-center text-sm font-medium border border-gray-200 rounded" min="0" data-testid={`qty-input-${item.key}`} />
                                            <button onClick={() => updateQty(item.key, 1)} className="w-6 h-6 rounded border border-gray-200 flex items-center justify-center hover:bg-gray-50" data-testid={`qty-plus-${item.key}`}><Plus size={12} /></button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <div className="flex flex-col items-center">
                                            <span className={`text-sm font-bold ${item.current_stock <= 0 ? 'text-red-500' : 'text-green-700'}`}>{item.current_stock}</span>
                                            <span className="text-[10px] text-gray-400">mevcut</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3"><button onClick={() => remove(item.key)} className="w-6 h-6 rounded bg-red-50 flex items-center justify-center hover:bg-red-100" data-testid={`remove-${item.key}`}><Trash2 size={12} className="text-red-500" /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center gap-3">
                    <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notlar..." className="form-input flex-1 text-sm" data-testid="notes-input" />
                    <button onClick={handlePrint} disabled={!cart.length} className="btn btn-secondary px-3" data-testid="print-btn"><Printer size={16} /></button>
                    <div className="text-sm text-gray-500 min-w-[100px]"><span className="font-medium text-gray-900">{cart.length}</span> urun · <span className="font-medium text-gray-900">{total()}</span> adet</div>
                    <button onClick={handleSave} disabled={!cart.length || saving} className={`btn px-6 py-2.5 text-base ${mode === 'IN' ? 'btn-success' : 'btn-danger'}`} data-testid="save-btn">{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                </div>

                {recentOps.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="recent-operations">
                        <h3 className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-3"><Clock size={14} /> Son Islemler</h3>
                        <div className="space-y-2">
                            {recentOps.map(m => (
                                <div key={m.id} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${m.movement_type === 'IN' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>{m.movement_type === 'IN' ? '+' : '-'}</span>
                                        <span className="text-gray-700 truncate max-w-[300px]">{m.product_names}</span>
                                        {m.is_production && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">Uretim</span>}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`font-medium ${m.movement_type === 'IN' ? 'text-green-600' : 'text-red-500'}`}>{m.movement_type === 'IN' ? '+' : '-'}{m.total_qty}</span>
                                        <span className="text-xs text-gray-400">{new Date(m.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Variant Qty Picker */}
            {variantPicker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setVariantPicker(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()} data-testid="variant-picker" role="dialog" aria-label="Varyant secimi">
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <h3 className="font-medium text-gray-900">{variantPicker.name}</h3>
                            <button onClick={() => setVariantPicker(null)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"><X size={20} className="text-gray-500" /></button>
                        </div>
                        <div className="p-3 max-h-[350px] overflow-y-auto space-y-1">
                            {variantPicker.variants?.map(v => (
                                <div key={v.id} className="flex items-center justify-between p-3 rounded-lg" data-testid={`variant-pick-${v.id}`}>
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">{v.name}</div>
                                        <div className="text-xs text-gray-400">{v.sku || v.barcode || ''} · Stok: {v.current_stock || 0}</div>
                                    </div>
                                    <input
                                        type="number"
                                        min="0"
                                        value={variantQtys[v.id] || 0}
                                        onChange={e => setVariantQtys(p => ({ ...p, [v.id]: Math.max(0, parseInt(e.target.value) || 0) }))}
                                        className="w-16 h-8 text-center text-sm font-medium border border-gray-200 rounded-lg"
                                        data-testid={`variant-qty-${v.id}`}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="p-4 border-t border-gray-100 flex gap-3">
                            <button onClick={() => setVariantPicker(null)} className="btn btn-secondary flex-1 text-sm">Iptal</button>
                            <button onClick={confirmVariantPicker} className="btn btn-primary flex-1 text-sm" data-testid="confirm-variants-btn">Ekle</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Scanner */}
            {showScanner && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" role="dialog" aria-label="Barkod tarayici">
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <h3 className="font-medium text-gray-900">Barkod Tara</h3>
                            <button onClick={closeScanner} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"><X size={20} className="text-gray-500" /></button>
                        </div>
                        <div className="p-4">
                            <div id="scanner-container" style={{ width: '100%', minHeight: '250px', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#000' }} />
                            {!scannerReady && <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" /></div>}
                        </div>
                        <div className="p-4 bg-gray-50 text-center"><p className="text-sm text-gray-500">Barkodu kameraya tutun</p></div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}
